import { c as sanitizeMetadata, l as validateEventName } from "./sanitizer-CLnKD_rP.js";
import { createContext, useCallback, useContext } from "react";
import { useStatsigClient } from "@statsig/react-bindings";

//#region src/context.ts
const DEFAULT_SETTINGS = { debugLogEvents: false };
const StatsigSettingsContext = createContext(DEFAULT_SETTINGS);
function useStatsigSettings() {
	return useContext(StatsigSettingsContext);
}

//#endregion
//#region src/hooks/useTrackEvent.ts
/**
* HIPAA-compliant event tracker.
*
* All metadata passes through `sanitizeMetadata` before reaching Statsig.
* Event names are validated against known PHI patterns and dropped if invalid.
*
* Debug logging (console.debug) is controlled by the `debugLogEvents` setting
* on `<StatsigProvider>`.
*/
function useTrackEvent() {
	const { client } = useStatsigClient();
	const { debugLogEvents } = useStatsigSettings();
	const trackEvent = useCallback((eventName, options) => {
		try {
			if (!validateEventName(eventName)) {
				console.error(`[Analytics] Invalid event name: ${eventName}`);
				return;
			}
			const sanitizedMetadata = sanitizeMetadata(options?.metadata);
			client.logEvent(eventName, options?.value, sanitizedMetadata);
			if (debugLogEvents) console.debug("[Analytics] Event tracked:", {
				eventName,
				value: options?.value,
				metadata: sanitizedMetadata
			});
		} catch (error) {
			console.error("[Analytics] Failed to track event:", error);
		}
	}, [client, debugLogEvents]);
	return {
		trackEvent,
		trackError: useCallback((error, context) => {
			trackEvent("error_occurred", { metadata: {
				error_message: error instanceof Error ? error.message : String(error),
				error_type: context || "unknown",
				timestamp: (/* @__PURE__ */ new Date()).toISOString()
			} });
		}, [trackEvent]),
		trackTiming: useCallback((label, duration, metadata) => {
			trackEvent("performance_timing", {
				value: Math.round(duration),
				metadata: {
					action_type: label,
					duration_ms: String(Math.round(duration)),
					...metadata
				}
			});
		}, [trackEvent])
	};
}

//#endregion
export { StatsigSettingsContext as n, useStatsigSettings as r, useTrackEvent as t };
//# sourceMappingURL=useTrackEvent-IE9Pu1uh.js.map